// pages/SuccessPage.js
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import html2pdf from "html2pdf.js";
import sendOrderConfirmationEmail from "../components/sendMail";
// import { sendEmailWithTemplate } from '../utils/emailService';

const SuccessPage = () => {
  const location = useLocation();
  const {
    orderId,
    userEmail,
    userName,
    phone,
    items,
    totalAmount,
    examData, // Detailed exam data with papers
    selectedExams, // Raw selected exam keys
    allExamData, // Full EXAM_DATA reference
  } = location.state || {
    orderId: "ORD" + Math.floor(Math.random() * 1000000),
    userEmail: "customer@example.com",
    userName: "Customer",
    phone: "",
    items: [],
    totalAmount: 0,
    examData: [],
    selectedExams: [],
    allExamData: {},
  };

  const [downloadProgress, setDownloadProgress] = useState({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [emailStatus, setEmailStatus] = useState({
    sent: false,
    sending: false,
    error: null
  });
  
  useEffect(() => {
    // Send confirmation email with download links
    if (userEmail && examData && examData.length > 0) {
      handleSendEmail();
    }
  }, []);
  
  const handleSendEmail = async () => {
    setEmailStatus({ sent: false, sending: true, error: null });
    
    try {
      const result = await sendOrderConfirmationEmail({
        orderId,
        userEmail, 
        userName,
        totalAmount,
        examData
      });
      
      if (result.success) {
        setEmailStatus({ sent: true, sending: false, error: null });
      } else {
        setEmailStatus({ sent: false, sending: false, error: result.message });
      }
    } catch (error) {
      setEmailStatus({ sent: false, sending: false, error: error.message });
    }
  };

  useEffect(() => {
    // Send confirmation email with download links
    // This would typically be handled by your backend
    if (userEmail) {
      const emailData = {
        to: userEmail,
        subject: "Your Samai Mocktest Purchase",
        orderDetails: {
          orderId,
          items,
          date: new Date().toLocaleDateString(),
        },
        downloadLinks:
          examData && examData.length > 0
            ? examData.map((exam) => ({
                name: exam.name,
                links: exam.papers.map((paper) => ({
                  name: paper,
                  url: `/mocktest-files/${exam.id}/${paper
                    .replace(/\s+/g, "-")
                    .toLowerCase()}.pdf`,
                })),
              }))
            : [],
      };

      // Uncomment if email service is available
      // sendEmailWithTemplate(emailData)
      //   .then(() => console.log('Email sent successfully'))
      //   .catch(error => console.error('Failed to send email:', error));
    }
  }, [orderId, userEmail, items, examData]);

  const generateInvoice = () => {
    const invoiceContent = document.getElementById("invoice-content");

    const opt = {
      margin: 1,
      filename: `samai-invoice-${orderId}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(invoiceContent).save();
  };

  // Function to simulate download progress
  // const startDownload = (examId, paperIndex = null) => {
  //   const examKey = paperIndex !== null ? `${examId}-${paperIndex}` : examId;

  //   // Set initial progress
  //   setDownloadProgress(prev => ({
  //     ...prev,
  //     [examKey]: 0
  //   }));

  //   // Simulate download progress
  //   const interval = setInterval(() => {
  //     setDownloadProgress(prev => {
  //       const currentProgress = prev[examKey] || 0;
  //       if (currentProgress >= 100) {
  //         clearInterval(interval);
  //         return prev;
  //       }
  //       return {
  //         ...prev,
  //         [examKey]: Math.min(currentProgress + 20, 100)
  //       };
  //     });
  //   }, 300);

  //   // Clear interval after simulated completion
  //   setTimeout(() => {
  //     clearInterval(interval);
  //     setDownloadProgress(prev => ({
  //       ...prev,
  //       [examKey]: 100
  //     }));
  //   }, 1500);
  // };

  // // Function to download all papers for an exam
  // const downloadAllPapers = (exam) => {
  //   // Set download all in progress
  //   setIsDownloadingAll(true);

  //   // Start individual downloads with slight delay between them
  //   exam.papers.forEach((paper, index) => {
  //     setTimeout(() => {
  //       startDownload(exam.id, index);
  //     }, index * 500);
  //   });

  //   // Reset downloading all state after completion
  //   setTimeout(() => {
  //     setIsDownloadingAll(false);
  //   }, (exam.papers.length * 500) + 1500);
  // };
  const startDownload = (examId, paperIndex = null) => {
    const examKey = paperIndex !== null ? `${examId}-${paperIndex}` : examId;

    // Set initial progress
    setDownloadProgress((prev) => ({
      ...prev,
      [examKey]: 0,
    }));

    // Create a URL to the file
    const paper = examData.find((exam) => exam.id === examId)?.papers[
      paperIndex
    ];
    if (!paper) return;

    // Create file path
    const filePath = `/mocktest-files/${examId}/${paper
      .replace(/\s+/g, "-")
      .toLowerCase()}.pdf`;

    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = filePath;
    link.download = paper.replace(/\s+/g, "-").toLowerCase() + ".pdf";

    // Simulate progress for better UX (in a real app, you'd track actual download progress)
    const interval = setInterval(() => {
      setDownloadProgress((prev) => {
        const currentProgress = prev[examKey] || 0;
        if (currentProgress >= 100) {
          clearInterval(interval);
          return prev;
        }
        return {
          ...prev,
          [examKey]: Math.min(currentProgress + 20, 100),
        };
      });
    }, 300);

    // Append to body, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Complete the progress bar after a short delay
    setTimeout(() => {
      clearInterval(interval);
      setDownloadProgress((prev) => ({
        ...prev,
        [examKey]: 100,
      }));
    }, 1500);
  };

  // Function to download all papers for an exam
  const downloadAllPapers = (exam) => {
    // Set download all in progress
    setIsDownloadingAll(true);

    // Download each paper with a slight delay between them
    exam.papers.forEach((paper, index) => {
      setTimeout(() => {
        startDownload(exam.id, index);
      }, index * 800); // Increased delay to prevent browser blocking multiple downloads
    });

    // Reset downloading all state after completion
    setTimeout(() => {
      setIsDownloadingAll(false);
    }, exam.papers.length * 800 + 1500);
  };
  // Function to get download progress for a specific paper
  const getDownloadProgress = (examId, paperIndex) => {
    return downloadProgress[`${examId}-${paperIndex}`] || 0;
  };

  return (
    <div className="py-12 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <svg
                className="w-16 h-16 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Payment Successful!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for purchasing Samai Mocktests. Your order has been
              confirmed.
            </p>
          </div>

          <div
            id="invoice-content"
            className="border border-gray-200 rounded-lg p-4 sm:p-6 mb-8"
          >
            {/* Fixed header for mobile view */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
              <div className="mb-4 sm:mb-0">
                <h2 className="text-xl font-bold text-gray-800">Invoice</h2>
                <p className="text-gray-600">Order ID: {orderId}</p>
                <p className="text-gray-600">
                  Date: {new Date().toLocaleDateString()}
                </p>
                <p className="text-gray-600">Name: {userName}</p>
                {phone && <p className="text-gray-600">Phone: {phone}</p>}
                {userEmail && (
                  <p className="text-gray-600">Email: {userEmail}</p>
                )}
              </div>

              {/* Logo section - fixed for mobile */}
              <div className="flex flex-row sm:flex-col items-center sm:items-end">
                <div className="flex items-center">
                  <img className="h-8 w-auto" src="/logo.png" alt="Samai" />
                  <span className="ml-2 text-xl font-bold bg-gradient-to-r from-[#4636FF] to-[#F253EC] bg-clip-text text-transparent">
                    Samai
                  </span>
                </div>
                <p className="text-gray-600 ml-2 sm:ml-0 sm:text-right">
                  samai.scontinent.com
                </p>
              </div>
            </div>

            <div className="border-t border-b border-gray-200 py-4 mb-6">
              <h3 className="font-semibold mb-3">Order Details:</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2">Item</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items && items.length > 0 ? (
                      items.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="py-2">{item.name}</td>
                          <td className="text-right py-2">₹{item.price}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-b border-gray-200">
                        <td className="py-2">Mock Test Bundle</td>
                        <td className="text-right py-2">₹99</td>
                      </tr>
                    )}
                    <tr>
                      <td className="py-2 font-bold">Total</td>
                      <td className="text-right py-2 font-bold">
                        ₹
                        {totalAmount ||
                          (items && items.length > 0
                            ? items.reduce((sum, item) => sum + item.price, 0)
                            : 99)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-gray-600 text-sm">
              Thank you for your purchase. For any queries, please contact
              office@scontinent.com
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Your Mock Tests</h3>

            {examData && examData.length > 0 ? (
              <div className="space-y-6">
                {examData.map((exam, examIndex) => (
                  <div key={examIndex} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start mb-3">
                      <h4 className="font-semibold text-gray-800 text-lg mb-2 sm:mb-0">
                        {exam.name}
                      </h4>
                      <button
                        onClick={() => downloadAllPapers(exam)}
                        disabled={isDownloadingAll}
                        className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 transition flex items-center"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                          ></path>
                        </svg>
                        Download All
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {exam.papers.map((paper, paperIndex) => (
                        <div
                          key={paperIndex}
                          className="border border-gray-200 rounded-md p-3 bg-white"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-800 break-words mr-2">
                              {paper}
                            </span>
                            {/* <a 
                              href={`/mocktest-files/${exam.id}/${paper.replace(/\s+/g, '-').toLowerCase()}.pdf`} 
                              download
                              onClick={(e) => {
                                // e.preventDefault();
                                startDownload(exam.id, paperIndex);
                              }}
                              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
                              </svg>
                            </a> */}
                            <a
                              href={`/mocktest-files/${exam.id}/${paper
                                .replace(/\s+/g, "-")
                                .toLowerCase()}.pdf`}
                              download={`${paper
                                .replace(/\s+/g, "-")
                                .toLowerCase()}.pdf`}
                              onClick={(e) => {
                                e.preventDefault();
                                startDownload(exam.id, paperIndex);
                              }}
                              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                                ></path>
                              </svg>
                            </a>
                          </div>

                          {/* Download progress bar */}
                          {getDownloadProgress(exam.id, paperIndex) > 0 && (
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                              <div
                                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-200"
                                style={{
                                  width: `${getDownloadProgress(
                                    exam.id,
                                    paperIndex
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                <p>No exams found in your order</p>
              </div>
            )}

            {userEmail && (
              <p className="text-sm text-gray-600 mt-4">
                All mock test files have also been sent to your email address (
                {userEmail})
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <button
              onClick={generateInvoice}
              className="bg-[#4636FF] text-white py-2 px-6 rounded-md hover:bg-opacity-90 transition duration-300 flex items-center justify-center"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                ></path>
              </svg>
              Download Invoice
            </button>
            <Link
              to="/"
              className="bg-gray-200 text-gray-800 py-2 px-6 rounded-md hover:bg-gray-300 transition duration-300 text-center"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
